import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Button,
  Checkbox,
  Drawer,
  InlineError,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { leadHref } from '../profiles/profileLinks';
import { LinkedInView } from './LinkedInView';
import { ProspectTierChip } from './ProspectTierChip';
import { ProspectDrawer } from './ProspectDrawer';
import { DmDailyCap, ProspectRowDmActions } from './ProspectDmActions';
import { buildProspectDmFunnel, buildTier1Funnel, prospectDmTaggedLine } from './prospectDmFunnel';
import { useTier1JourneyEvidence } from './linkedinJourney';
import type { ProspectDmStage, Tier1Stage } from './prospectDmFunnel';
import { FunnelRow } from './FunnelRow';
import { FUNNEL_EXPLAINERS, buildProspectFunnel } from './prospectFunnel';
import {
  UNBATCHED,
  batchErrorMessage,
  filterByBatch,
  groupBatches,
  recentlyCompletedBatches,
  unbatchedCount,
  planBatches,
  useCreateProspectBatch,
  useProspectBatches,
  useSetProspectBatch,
  useMoveProspectsBatch,
  useRenameProspectBatch,
} from './prospectBatches';
import type { BatchFilter, BatchGroup } from './prospectBatches';
import {
  batchButtonLabel,
  batchButtonReason,
  latestByProspect,
  queuedConfirmation,
  useQueueBatchResearch,
  useResearchPlan,
  useResearchQueue,
} from './researchQueue';
import { RESEARCH_BUCKETS, inResearchBucket, researchCounts, researchToday } from './researchStrip';
import type { ResearchBucket, ResearchCounts } from './researchStrip';
import {
  PROSPECT_FILTERS,
  filterProspects,
  filterProspectsByTier,
  searchProspects,
  sortProspects,
} from './prospectFilters';
import type { ProspectFilter, ProspectTierFilter } from './prospectFilters';
import { IMPORT_SOURCES, importSourceDateMeaning, parseProspectImport } from './prospectImport';
import { dedupProspectRows } from './prospectDedup';
import type { ImportSource } from './prospectImport';
import {
  PROSPECT_STATUSES,
  PROSPECT_TIERS,
  isProspectTier,
  tierResultMessage,
  useSetProspectTier,
  isProspectStatus,
  prospectErrorMessage,
  prospectName,
  useAddProspects,
  useProspects,
  useProspectFunnelRows,
  useSetProspectStatus,
} from './prospects';
import type { Prospect, ProspectTier } from './prospects';
import type { ProspectBatch } from './prospectBatches';
import './prospecting.css';

/**
 * The Prospecting tab — stage zero of the funnel.
 *
 * Hanneza, 9 September: "We only need to see the actionable columns on the
 * screen: Full Name, Company, Job Title, LinkedIn URL, Why Relevant, and
 * Opening Hook," with "a simple dropdown next to each name to track where
 * they are." Those six columns, that dropdown, and a way to promote.
 *
 * The LinkedIn daily counter keeps its place at the top (design note, drop
 * 2): it measures volume, this list measures people. Its history sits below.
 */
export function ProspectingView() {
  const [searchParams] = useSearchParams();
  const prospects = useProspects();
  const dmRows = useProspectFunnelRows();
  const [dmPick, setDmPick] = useState<
    { tier: 'tier1'; key: Tier1Stage } | { tier: 'tier2'; key: ProspectDmStage } | null
  >(null);
  const [filter, setFilter] = useState<ProspectFilter>('working');
  const [tierFilter, setTierFilter] = useState<ProspectTierFilter>('all');
  const [tierTarget, setTierTarget] = useState<ProspectTier | null>('tier2');
  const [tierMessage, setTierMessage] = useState('');
  const setTier = useSetProspectTier();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(() => searchParams.get('prospect'));
  const [adding, setAdding] = useState(false);

  const batches = useProspectBatches();
  const [batchId, setBatchId] = useState<BatchFilter>(() => searchParams.get('batch'));
  const [batchError, setBatchError] = useState('');
  const research = useResearchQueue();
  const [researchPick, setResearchPick] = useState<ResearchBucket | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [moveTarget, setMoveTarget] = useState('');
  const [moveMessage, setMoveMessage] = useState('');
  const move = useMoveProspectsBatch();
  useEffect(() => {
    setSelected([]);
  }, [batchId, filter, tierFilter, researchPick, query, dmPick]);

  const everyone = prospects.data ?? [];
  // The batch frames the funnel and research counts. Tier narrows the chips
  // and table within that batch without changing those summaries.
  const batch = filterByBatch(everyone, batchId);
  const all = filterProspectsByTier(batch, tierFilter);
  const tier1Journeys = useTier1JourneyEvidence(batch);
  // The research strip counts the same people the funnel does: the batch.
  const today = researchToday(new Date());
  const latest = latestByProspect(research.data ?? []);
  const researchTally = researchCounts(
    latest,
    batch.map((prospect) => prospect.id),
    today,
  );
  // A picked research count replaces the status chip rather than stacking on
  // it: "Stuck" means every stuck person in the batch, whatever their stage,
  // and a hidden chip quietly narrowing that would make the number lie.
  const narrowed =
    researchPick === null
      ? filterProspects(all, filter)
      : all.filter((prospect) => inResearchBucket(latest, prospect.id, researchPick, today));
  const batchIds = new Set(batch.map((prospect) => prospect.id));
  const dmFunnel = buildProspectDmFunnel(
    (dmRows.data ?? []).filter((row) => row.prospect_id !== null && batchIds.has(row.prospect_id)),
  );
  const tier1Funnel = buildTier1Funnel(
    batch,
    (dmRows.data ?? []).filter((row) => row.prospect_id !== null && batchIds.has(row.prospect_id)),
    tier1Journeys.data,
  );
  const selectedDmFunnel = dmPick?.tier === 'tier1' ? tier1Funnel : dmFunnel;
  const dmIds = selectedDmFunnel.stages.find((stage) => stage.key === dmPick?.key)?.ids ?? [];
  // A DM drill is exactly its displayed IDs, including promoted people.
  const shown =
    dmPick === null
      ? sortProspects(searchProspects(narrowed, query))
      : sortProspects(batch.filter((prospect) => dmIds.includes(prospect.id)));
  const eligible = shown.filter((prospect) => prospect.promoted_at === null);
  const ticked = eligible
    .filter((prospect) => selected.includes(prospect.id))
    .map((prospect) => prospect.id);
  const open = everyone.find((prospect) => prospect.id === openId) ?? null;
  // The funnel reads the whole batch before tier, status and search narrow it.
  const funnel = buildProspectFunnel(batch);
  const groups = groupBatches(batches.data ?? [], everyone);
  useEffect(() => {
    if (
      batches.data !== undefined &&
      batchId !== null &&
      batchId !== UNBATCHED &&
      !batches.data.some((batch) => batch.id === batchId)
    )
      setBatchId(null);
  }, [batches.data, batchId]);
  const loose = unbatchedCount(everyone);
  const recent = recentlyCompletedBatches(
    batches.data ?? [],
    everyone,
    research.data ?? [],
    new Date(),
  );

  return (
    <LinkedInView>
      {/* The batch picker sits above everything it frames, on its own line.
          Kyu's screenshot of the mockup showed it colliding with the status
          chips at a narrow width and cutting the first one off — so it is not
          in that row, it is over it. */}
      <BatchPicker
        groups={groups}
        looseCount={loose}
        total={everyone.length}
        value={batchId}
        onPick={setBatchId}
        onError={setBatchError}
      />
      {batchError === '' ? null : <InlineError>{batchError}</InlineError>}

      {/* Hanneza, 23 September: she has to remember a batch's exact name in
          the dropdown above to find yesterday's finished research. This
          reads the same research-queue rows the strip below already loads
          and shows batches with a "done" result in the last seven days, so
          she can jump straight to one without scrolling or typing a name. */}
      {recent.length === 0 ? null : (
        <div className="prospecting-recent">
          <span className="prospecting-recent-label">Recently completed</span>
          {recent.map(({ batch, count }) => (
            <button
              key={batch.id}
              type="button"
              className="prospecting-recent-pill"
              aria-pressed={batchId === batch.id}
              onClick={() => setBatchId(batch.id)}
            >
              {batch.name} <span>{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* One button for the whole batch. It says what it is about to do rather
          than hiding the 90-day rule: press February today and it reads
          "Research and draft 131 of 171", press it in three months and it
          reads 171 of 171. */}
      <BatchResearchButton batchId={typeof batchId === 'string' ? batchId : null} />

      <ResearchStrip
        counts={researchTally}
        loading={research.isPending}
        failed={research.isError}
        picked={researchPick}
        onPick={(bucket) => {
          setDmPick(null);
          setResearchPick((current) => (current === bucket ? null : bucket));
        }}
      />

      {/* The Pipeline tab's Outcome panel, for prospects. Same rows, same
          classes, so the two funnels read as one thing. Each row is the
          filter for the people it counts: clicking Connected shows everyone
          who reached Connected or went past it, which is what its number
          says. The chips underneath answer the other question, who is
          sitting at a stage right now. See prospectFilters.ts. */}
      <section className="pipeline-funnels prospecting-funnels">
        <article className="pipeline-card">
          <header className="pipeline-card-head">
            <h3>Funnel</h3>
            <p>Where the outreach stands. Click a stage to see those people.</p>
          </header>
          <ul className="pipeline-funnel">
            {funnel.stages.map((line) => (
              <li key={line.key}>
                <FunnelRow
                  label={line.label}
                  explainer={FUNNEL_EXPLAINERS[line.key]}
                  count={line.count}
                  percent={line.percent}
                  tone={line.key === 'promoted' ? 'won' : 'plain'}
                  on={researchPick === null && filter === line.filter}
                  onPick={() => {
                    setDmPick(null);
                    setResearchPick(null);
                    setFilter(line.filter);
                  }}
                />
              </li>
            ))}
          </ul>
        </article>
        <article className="pipeline-card">
          <header className="pipeline-card-head">
            <h3>Dead ends</h3>
            <p>Left the funnel. Not counted above.</p>
          </header>
          <ul className="pipeline-funnel">
            {funnel.deadEnds.map((line) => (
              <li key={line.key}>
                <FunnelRow
                  label={line.label}
                  explainer={FUNNEL_EXPLAINERS[line.key]}
                  count={line.count}
                  percent={line.percent}
                  tone="lost"
                  on={researchPick === null && filter === line.filter}
                  onPick={() => {
                    setDmPick(null);
                    setResearchPick(null);
                    setFilter(line.filter);
                  }}
                />
              </li>
            ))}
          </ul>
        </article>
      </section>

      <DmDailyCap />
      {dmRows.isError ? (
        <InlineError>DM funnel could not be loaded. Refresh to try again.</InlineError>
      ) : dmRows.isPending ? (
        <Skeleton lines={2} label="Loading DM funnel" />
      ) : (
        <>
          <section className="pipeline-funnels prospecting-funnels">
            <article className="pipeline-card">
              <header className="pipeline-card-head">
                <h3>Tier 2 DM funnel</h3>
                <p>{prospectDmTaggedLine(dmFunnel)}</p>
                <p>Activity only. Click a stage to see those people.</p>
              </header>
              <ul className="pipeline-funnel">
                {dmFunnel.stages.map((line) => (
                  <li key={line.key}>
                    <FunnelRow
                      label={line.label}
                      explainer={line.explainer}
                      count={line.count}
                      percent={line.percent}
                      tone={line.key === 'promoted' ? 'won' : 'plain'}
                      on={dmPick?.tier === 'tier2' && dmPick.key === line.key}
                      onPick={() => {
                        setDmPick((current) =>
                          current?.tier === 'tier2' && current.key === line.key
                            ? null
                            : { tier: 'tier2', key: line.key },
                        );
                        setResearchPick(null);
                        setFilter('all');
                        setTierFilter('all');
                        setQuery('');
                      }}
                    />
                  </li>
                ))}
              </ul>
            </article>
          </section>
          <section className="pipeline-funnels prospecting-funnels">
            <article className="pipeline-card">
              <header className="pipeline-card-head">
                <h3>Tier 1 DM funnel</h3>
                <p>On hold until the Plastic Neutrality offer is ready.</p>
                <p>{prospectDmTaggedLine(tier1Funnel)}</p>
              </header>
              {tier1Journeys.isError ? (
                <InlineError>Tier 1 journey could not be loaded. Refresh to try again.</InlineError>
              ) : tier1Journeys.isPending ? (
                <Skeleton lines={2} label="Loading Tier 1 journey" />
              ) : (
                <ul className="pipeline-funnel">
                  {tier1Funnel.stages.map((line) => (
                    <li key={line.key}>
                      <FunnelRow
                        label={line.label}
                        explainer={line.explainer}
                        count={line.count}
                        percent={line.percent}
                        tone={line.key === 'won' ? 'won' : 'plain'}
                        on={dmPick?.tier === 'tier1' && dmPick.key === line.key}
                        onPick={() => {
                          setDmPick((current) =>
                            current?.tier === 'tier1' && current.key === line.key
                              ? null
                              : { tier: 'tier1', key: line.key },
                          );
                          setResearchPick(null);
                          setFilter('all');
                          setTierFilter('all');
                          setQuery('');
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </article>
          </section>
        </>
      )}
      {dmPick !== null && (
        <p>
          Showing the {dmPick.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}{' '}
          {selectedDmFunnel.stages.find((line) => line.key === dmPick.key)?.label} list.{' '}
          <Button size="sm" variant="ghost" onClick={() => setDmPick(null)}>
            Clear DM filter
          </Button>
        </p>
      )}

      <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="prospecting-head">
          <p className="tracker-dashboard-card-title" style={{ margin: 0 }}>
            Prospects
          </p>
          <div className="prospecting-head-tools">
            <Input
              label="Search prospects"
              labelHidden
              placeholder="Search name, company, title"
              value={query}
              onChange={(e) => {
                setDmPick(null);
                setQuery(e.target.value);
              }}
            />
            <Button size="sm" onClick={() => setAdding(true)}>
              Import CSV
            </Button>
          </div>
        </div>

        <div className="prospecting-filters">
          {PROSPECT_FILTERS.map((entry) => {
            const n = filterProspects(all, entry.key).length;
            return (
              <Button
                key={entry.key}
                size="sm"
                variant={researchPick === null && filter === entry.key ? 'primary' : 'secondary'}
                aria-pressed={researchPick === null && filter === entry.key}
                onClick={() => {
                  setDmPick(null);
                  setResearchPick(null);
                  setFilter(entry.key);
                }}
              >
                {entry.label} <span className="timeline-filter-count">{n}</span>
              </Button>
            );
          })}
        </div>

        <div className="prospecting-filters">
          <Select
            label="Filter by tier"
            value={tierFilter}
            onChange={(e) => {
              setDmPick(null);
              setTierFilter(e.target.value as ProspectTierFilter);
            }}
            options={[
              { value: 'all', label: 'All tiers (Trash hidden)' },
              { value: 'tier1', label: 'Tier 1' },
              { value: 'tier2', label: 'Tier 2' },
              { value: 'untagged', label: 'Not tagged' },
              { value: 'trash', label: 'Show Trash' },
            ]}
          />
        </div>
        {tierMessage === '' ? null : <p role="status">{tierMessage}</p>}

        {prospects.isPending ? (
          <Skeleton lines={4} label="Loading prospects" />
        ) : prospects.isError ? (
          <InlineError>{prospectErrorMessage(prospects.error.message)}</InlineError>
        ) : shown.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {batch.length === 0
              ? 'No prospects yet. Bring in Senku’s sheet or a LinkedIn export with Import CSV.'
              : 'Nothing here under this filter.'}
          </p>
        ) : (
          <>
            <div className="prospecting-head-tools">
              <Checkbox
                label="Select all shown"
                disabled={move.isPending || setTier.isPending || eligible.length === 0}
                checked={eligible.length > 0 && ticked.length === eligible.length}
                onChange={(e) =>
                  setSelected(e.target.checked ? eligible.map((prospect) => prospect.id) : [])
                }
              />
              <Select
                label="Move to batch"
                value={moveTarget}
                disabled={move.isPending || setTier.isPending}
                onChange={(e) => setMoveTarget(e.target.value)}
                options={[{ value: '', label: 'No batch' }]}
                groups={groups.map((group) => ({
                  label: group.label,
                  options: group.batches.map(({ batch }) => ({
                    value: batch.id,
                    label: batch.name,
                  })),
                }))}
              />
              <Button
                size="sm"
                disabled={move.isPending || setTier.isPending || ticked.length === 0}
                onClick={() => {
                  setMoveMessage('');
                  move.mutate(
                    { prospectIds: ticked, batchId: moveTarget === '' ? null : moveTarget },
                    {
                      onSuccess: (result) => {
                        setSelected([]);
                        setMoveMessage(
                          `Moved ${String(result.moved)} of ${String(result.total)}.${result.error === '' ? '' : ` The rest were not moved: ${result.error}.`}`,
                        );
                      },
                      onError: (cause) => {
                        setSelected([]);
                        setMoveMessage(
                          `Moved 0 of ${String(ticked.length)}. The rest were not moved: ${cause.message}.`,
                        );
                      },
                    },
                  );
                }}
              >
                {move.isPending ? 'Moving…' : `Move ${String(ticked.length)}`}
              </Button>
            </div>
            <div className="prospecting-head-tools">
              <Select
                label="Set tier"
                value={tierTarget ?? ''}
                disabled={setTier.isPending || move.isPending}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === '' || isProspectTier(next)) setTierTarget(next === '' ? null : next);
                }}
                options={[
                  ...PROSPECT_TIERS.map((entry) => ({ value: entry.key, label: entry.label })),
                  { value: '', label: 'Clear' },
                ]}
              />
              <Button
                size="sm"
                disabled={setTier.isPending || move.isPending || ticked.length === 0}
                onClick={() => {
                  setTierMessage('');
                  setTier.mutate(
                    { ids: ticked, tier: tierTarget },
                    {
                      onSuccess: (result) => {
                        setSelected([]);
                        setTierMessage(tierResultMessage(result));
                      },
                      onError: (cause) => setTierMessage(prospectErrorMessage(cause.message)),
                    },
                  );
                }}
              >
                {setTier.isPending ? 'Saving…' : 'Set tier'}
              </Button>
              {ticked.length === 0 ? null : (
                <span>
                  {tierTarget === null
                    ? 'Clear tier'
                    : `Set ${PROSPECT_TIERS.find((entry) => entry.key === tierTarget)?.label}`}{' '}
                  on {ticked.length} selected prospects?
                </span>
              )}
            </div>
            {moveMessage === '' ? null : <p role="status">{moveMessage}</p>}
            <ProspectTable
              prospects={shown}
              batches={batches.data ?? []}
              onOpen={setOpenId}
              selected={ticked}
              selectingDisabled={move.isPending || setTier.isPending}
              onSelect={(id, checked) =>
                setSelected((current) =>
                  checked ? [...current, id] : current.filter((value) => value !== id),
                )
              }
            />
          </>
        )}
      </div>

      <ProspectDrawer prospect={open} onClose={() => setOpenId(null)} />
      <AddProspectsDrawer
        open={adding}
        existing={everyone}
        batches={batches.data ?? []}
        onClose={() => setAdding(false)}
      />
    </LinkedInView>
  );
}

/**
 * Where Senku and Reigen's research stands for the people on the page (Drop
 * 237). Five counts, one per state a person's newest request can be in. Each
 * is a button: pressing one shows those people in the list below, pressing it
 * again clears it. See researchStrip.ts for what each count means.
 */
function ResearchStrip({
  counts,
  loading,
  failed,
  picked,
  onPick,
}: {
  counts: ResearchCounts;
  loading: boolean;
  failed: boolean;
  picked: ResearchBucket | null;
  onPick: (bucket: ResearchBucket) => void;
}) {
  if (failed) {
    return <InlineError>Research status could not be loaded. Refresh to try again.</InlineError>;
  }
  const asked = RESEARCH_BUCKETS.reduce((sum, bucket) => sum + counts[bucket.key], 0);
  return (
    <section className="prospecting-research" aria-label="Research status">
      <p className="prospecting-research-lead">
        Research <span>Senku + Reigen</span>
      </p>
      {loading ? (
        <p className="prospecting-research-empty">Loading research status…</p>
      ) : asked === 0 ? (
        <p className="prospecting-research-empty">No research requested for these prospects yet.</p>
      ) : (
        <ul className="prospecting-research-counts">
          {RESEARCH_BUCKETS.map((bucket) => (
            <li key={bucket.key}>
              <button
                type="button"
                className={`prospecting-research-count prospecting-research-count--${bucket.key}`}
                aria-pressed={picked === bucket.key}
                onClick={() => onPick(bucket.key)}
              >
                <b>{counts[bucket.key]}</b>
                <span>{bucket.label}</span>
                <small>{bucket.hint}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The batch picker: one select, grouped by source, months inside.
 *
 * A native <optgroup> select rather than a menu of buttons. Kyu will have
 * five batches this week and sixty by March, and a chip row does not survive
 * sixty of anything — the Lead Origin picker learned that already and became
 * a grouped dropdown for the same reason.
 *
 * "New batch…" is the last option rather than a button beside the select,
 * because making a batch and choosing one are the same motion: you go to the
 * list of batches either way.
 */
function BatchResearchButton({ batchId }: { batchId: string | null }) {
  const plan = useResearchPlan(batchId);
  const queue = useQueueBatchResearch();
  const [said, setSaid] = useState('');
  const [failed, setFailed] = useState('');

  // No batch picked means "All prospects" or "No batch", and queueing either of
  // those is a decision nobody made on purpose.
  if (batchId === null) return null;

  const nothingDue = plan.data !== undefined && plan.data.due === 0;
  const reason = batchButtonReason(plan.data);

  return (
    <div className="prospecting-batch-action">
      <Button
        size="sm"
        variant="secondary"
        disabled={plan.isPending || nothingDue || queue.isPending}
        onClick={() => {
          setSaid('');
          setFailed('');
          queue.mutate(batchId, {
            onSuccess: (added) => setSaid(queuedConfirmation(added)),
            onError: (cause) => setFailed(cause.message),
          });
        }}
      >
        {batchButtonLabel(plan.data)}
      </Button>
      {said === '' ? null : <span className="prospecting-batch-note">{said}</span>}
      {said === '' && reason !== '' ? (
        <span className="prospecting-batch-note">{reason}</span>
      ) : null}
      {failed === '' ? null : <InlineError>{failed}</InlineError>}
    </div>
  );
}

function BatchPicker({
  groups,
  looseCount,
  total,
  value,
  onPick,
  onError,
}: {
  groups: BatchGroup[];
  looseCount: number;
  total: number;
  value: BatchFilter;
  onPick: (value: BatchFilter) => void;
  onError: (message: string) => void;
}) {
  const create = useCreateProspectBatch();
  const rename = useRenameProspectBatch();
  const picked = groups
    .flatMap((group) => group.batches)
    .find(({ batch }) => batch.id === value)?.batch;
  const NEW = '__new__';

  function handle(next: string) {
    onError('');
    if (next === NEW) {
      const name = window.prompt('Name the new batch');
      if (name === null || name.trim() === '') return;
      create.mutate(
        { name },
        {
          // Select it immediately: an empty batch you cannot see is a batch
          // you will make twice.
          onSuccess: (batch) => onPick(batch.id),
          onError: (cause) => onError(batchErrorMessage(cause.message)),
        },
      );
      return;
    }
    onPick(next === '' ? null : next);
  }

  return (
    <div className="prospecting-batchbar">
      <Select
        label="Batch"
        value={value ?? ''}
        disabled={create.isPending}
        onChange={(e) => handle(e.target.value)}
        options={[
          { value: '', label: `All prospects (${String(total)})` },
          ...(looseCount === 0
            ? []
            : [{ value: UNBATCHED, label: `No batch (${String(looseCount)})` }]),
        ]}
        groups={[
          ...groups.map((group) => ({
            label: group.label,
            options: group.batches.map(({ batch, count }) => ({
              value: batch.id,
              label: `${batch.name} (${String(count)})`,
            })),
          })),
          { label: ' ', options: [{ value: NEW, label: 'New batch…' }] },
        ]}
      />
      {picked === undefined ? null : (
        <Button
          size="sm"
          variant="secondary"
          disabled={rename.isPending}
          onClick={() => {
            const name = window.prompt('Rename batch', picked.name);
            if (name === null) return;
            onError('');
            rename.mutate(
              { batchId: picked.id, name },
              { onError: (cause) => onError(cause.message) },
            );
          }}
        >
          Rename batch
        </Button>
      )}
    </div>
  );
}

/**
 * The batch on one row. Moving somebody is one change, not a drag.
 *
 * A promoted prospect keeps its batch and loses the control: the work is on
 * the lead now, and refiling a row nobody works from is filing for its own
 * sake.
 */
function BatchCell({
  prospect,
  batches,
}: {
  prospect: Prospect;
  batches: readonly ProspectBatch[];
}) {
  const setBatch = useSetProspectBatch();
  const [error, setError] = useState('');
  const mine = batches.find((batch) => batch.id === prospect.batch_id);

  if (prospect.promoted_at !== null) {
    return (
      <span className="prospecting-promoted">{mine === undefined ? 'No batch' : mine.name}</span>
    );
  }

  return (
    <>
      <Select
        label={`Batch for ${prospectName(prospect)}`}
        labelHidden
        value={prospect.batch_id ?? ''}
        onChange={(e) => {
          const next = e.target.value;
          setError('');
          setBatch.mutate(
            { prospectId: prospect.id, batchId: next === '' ? null : next },
            { onError: (cause) => setError(batchErrorMessage(cause.message)) },
          );
        }}
        options={[
          { value: '', label: 'No batch' },
          ...batches.map((batch) => ({ value: batch.id, label: batch.name })),
        ]}
      />
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </>
  );
}

function ProspectTable({
  prospects,
  batches,
  onOpen,
  selected,
  selectingDisabled,
  onSelect,
}: {
  prospects: readonly Prospect[];
  batches: readonly ProspectBatch[];
  onOpen: (id: string) => void;
  selected: readonly string[];
  selectingDisabled: boolean;
  onSelect: (id: string, checked: boolean) => void;
}) {
  const setStatus = useSetProspectStatus();
  const [error, setError] = useState('');
  return (
    <>
      {error !== '' && <InlineError>{error}</InlineError>}
      <table className="tracker-perf-table prospecting-table">
        <colgroup>
          <col className="prospecting-col-name" />
          <col className="prospecting-col-company" />
          <col className="prospecting-col-title" />
          <col className="prospecting-col-link" />
          <col />
          <col />
          <col className="prospecting-col-batch" />
          <col className="prospecting-col-status" />
          <col className="prospecting-col-tier" />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Company</th>
            <th scope="col">Job title</th>
            <th scope="col">LinkedIn</th>
            <th scope="col">Why relevant</th>
            <th scope="col">Opening hook</th>
            <th scope="col">Batch</th>
            <th scope="col">Status</th>
            <th scope="col">Tier</th>
            <th scope="col">DM actions</th>
          </tr>
        </thead>
        <tbody>
          {prospects.map((prospect) => {
            const promoted = prospect.promoted_at !== null;
            return (
              <tr key={prospect.id}>
                <th scope="row">
                  {promoted ? null : (
                    <Checkbox
                      label={`Select ${prospectName(prospect)}`}
                      labelHidden
                      checked={selected.includes(prospect.id)}
                      disabled={selectingDisabled}
                      onChange={(e) => onSelect(prospect.id, e.target.checked)}
                    />
                  )}
                  <button
                    type="button"
                    className="prospecting-open"
                    onClick={() => onOpen(prospect.id)}
                  >
                    {prospectName(prospect)}
                  </button>
                </th>
                <td>{prospect.company_name}</td>
                <td>{prospect.title}</td>
                <td>
                  {prospect.linkedin_url === '' ? null : (
                    <a
                      className="profile-open"
                      href={prospect.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${prospectName(prospect)} on LinkedIn`}
                    >
                      Open
                    </a>
                  )}
                </td>
                <td>
                  <span className="prospecting-clamp">{prospect.why_relevant}</span>
                </td>
                <td>
                  <span className="prospecting-clamp">{prospect.opening_hook}</span>
                </td>
                <td>
                  <BatchCell prospect={prospect} batches={batches} />
                </td>
                <td>
                  {promoted ? (
                    <span className="prospecting-promoted">
                      Promoted
                      {prospect.promoted_lead_id === null ? null : (
                        <>
                          {' · '}
                          <Link className="profile-open" to={leadHref(prospect.promoted_lead_id)}>
                            Open lead
                          </Link>
                        </>
                      )}
                    </span>
                  ) : (
                    <Select
                      label={`Status for ${prospectName(prospect)}`}
                      labelHidden
                      value={prospect.status}
                      options={PROSPECT_STATUSES.map((entry) => ({
                        value: entry.key,
                        label: entry.label,
                      }))}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (!isProspectStatus(next)) return;
                        setError('');
                        setStatus.mutate(
                          { prospectId: prospect.id, status: next },
                          { onError: (cause) => setError(prospectErrorMessage(cause.message)) },
                        );
                      }}
                    />
                  )}
                </td>
                <td>
                  <ProspectTierChip tier={prospect.tier} />
                </td>
                <td>{promoted ? null : <ProspectRowDmActions prospect={prospect} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/**
 * Paste from a sheet. TSV straight from Google Sheets or CSV, header row
 * first; rows already in the Hub are skipped by LinkedIn URL or email.
 *
 * Drop C: which sheet is now a question, asked first, because the same three
 * columns mean different things depending on the answer. A blank Status in
 * Senku's sheet means nobody has touched them; in a LinkedIn connections
 * export every row is somebody who already accepted. And the date column
 * names the batch, so getting the source wrong files 364 people under the
 * wrong heading.
 */
function AddProspectsDrawer({
  open,
  existing,
  batches,
  onClose,
}: {
  open: boolean;
  existing: readonly Prospect[];
  batches: readonly ProspectBatch[];
  onClose: () => void;
}) {
  const add = useAddProspects();
  const [text, setText] = useState('');
  const [source, setSource] = useState<ImportSource>('senku_daily');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const [destination, setDestination] = useState('');
  const [newBatchName, setNewBatchName] = useState('');
  const [includePossible, setIncludePossible] = useState(false);
  const parsed = parseProspectImport(text, existing, source);
  const deduped = dedupProspectRows(parsed.rows, existing, includePossible);
  const rows = deduped.rows;
  // A fixed caption with a count, not a row label: it stays visible
  // (checkboxLabels.test.ts hides only labels that name one row).
  const includePossibleCaption = `Also add the ${String(deduped.possible.length)} possible duplicates`;
  const picked = batches.find((batch) => batch.id === destination);
  const invalidBatch =
    destination === '__new__'
      ? newBatchName.trim() === ''
      : destination !== '' && picked === undefined;
  const groups = groupBatches(batches, existing);
  const planned =
    destination === ''
      ? planBatches(rows, source)
      : rows.length === 0
        ? []
        : [
            {
              name: destination === '__new__' ? newBatchName.trim() : (picked?.name ?? ''),
              rows: rows.map((_row, index) => index),
            },
          ];
  const undated = destination === '' ? rows.filter((row) => row.sourced_on === null).length : 0;

  function close() {
    setText('');
    setMessage('');
    setError('');
    setFileName('');
    setDestination('');
    setNewBatchName('');
    setIncludePossible(false);
    onClose();
  }

  // A file is read into the same box a paste goes into, on purpose. The
  // preview, the duplicate count and the batch plan below all read that box,
  // so an uploaded file gets exactly the same look-before-you-add as a paste
  // instead of a second path that skips it. LinkedIn's own connections export
  // is comma-separated and parseProspectImport already reads commas when the
  // first line carries no tab, so the file needs no reformatting at all.
  async function readFile(file: File) {
    setMessage('');
    setError('');
    try {
      setText(await file.text());
      setFileName(file.name);
    } catch {
      setError(`Could not read ${file.name}.`);
    }
  }

  return (
    <Drawer open={open} title="Import prospects" onClose={close} wide>
      <p
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-secondary)',
        }}
      >
        Choose a CSV file, or paste the rows from a sheet including the header row. A LinkedIn
        connections export works as it comes. Recognized columns: Name (or First / Last), Company,
        Title or Position, LinkedIn URL or URL, Why Relevant, Opening Hook, Email, Phone, Status,
        and a date: Date or Connected On. People already in the Hub, including LinkedIn link
        variants, are skipped.
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <Button variant="ghost" size="sm" onClick={() => fileInput.current?.click()}>
          Choose file
        </Button>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
          {fileName === '' ? 'CSV or TSV' : fileName}
        </span>
        <input
          ref={fileInput}
          type="file"
          aria-label="CSV file"
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file !== undefined) void readFile(file);
          }}
        />
      </div>
      {error !== '' && <InlineError>{error}</InlineError>}
      <Select
        label="Where these come from"
        hint={importSourceDateMeaning(source)}
        value={source}
        onChange={(e) => {
          const next = e.target.value;
          if (next === 'senku_daily' || next === 'linkedin_connections' || next === 'old_tracker') {
            setSource(next);
            setMessage('');
          }
        }}
        options={IMPORT_SOURCES.map((entry) => ({ value: entry.key, label: entry.label }))}
      />
      <Select
        label="Put them in"
        value={destination}
        onChange={(e) => {
          setDestination(e.target.value);
          setMessage('');
          setError('');
        }}
        options={[{ value: '', label: 'Auto by date' }]}
        groups={[
          ...groups.map((group) => ({
            label: group.label,
            options: group.batches.map(({ batch }) => ({ value: batch.id, label: batch.name })),
          })),
          { label: ' ', options: [{ value: '__new__', label: 'New batch' }] },
        ]}
      />
      {destination === '__new__' ? (
        <Input
          label="New batch name"
          value={newBatchName}
          onChange={(e) => setNewBatchName(e.target.value)}
          hint={newBatchName.trim() === '' ? 'Give the batch a name.' : undefined}
        />
      ) : null}
      <Textarea
        label="Pasted rows"
        rows={10}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setFileName('');
          setMessage('');
        }}
      />
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
        {text.trim() === ''
          ? 'Nothing pasted yet.'
          : parsed.error !== ''
            ? parsed.error
            : `${String(rows.length)} new · ${String(parsed.duplicates + deduped.duplicates)} already in the Hub · ${String(deduped.possible.length)} possible duplicates · ${String(parsed.skipped)} empty`}
      </p>
      {deduped.possible.length === 0 ? null : (
        <>
          <Checkbox
            label={includePossibleCaption}
            checked={includePossible}
            onChange={(e) => setIncludePossible(e.target.checked)}
          />
          {includePossible ? null : (
            <section aria-label="Possible duplicates, not added">
              <p className="dash-group-label">Possible duplicates, not added</p>
              <ul>
                {deduped.possible.map((row, index) => (
                  <li key={index}>{prospectName(row)}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {/* What the import is about to DO, before it does it. 364 people
          arriving in the wrong months is not something you notice afterwards
          . You notice it a week later when a batch you worked turns out to
          have been the wrong one. */}
      {planned.length === 0 && undated === 0 ? null : (
        <div className="prospecting-import-plan" role="region" aria-label="Batches this will fill">
          <p className="dash-group-label">Batches this will fill</p>
          <ul>
            {planned.map((plan) => (
              <li key={plan.name}>
                <span>{plan.name}</span>
                <b>{plan.rows.length}</b>
              </li>
            ))}
            {undated === 0 ? null : (
              <li>
                <span>No batch, because the row carries no date</span>
                <b>{undated}</b>
              </li>
            )}
          </ul>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Button
          onClick={() =>
            add.mutate(
              {
                rows,
                batchSource: source,
                ...(destination === '__new__'
                  ? { newBatchName }
                  : destination === ''
                    ? {}
                    : { batchId: destination }),
              },
              {
                onSuccess: (result) => {
                  setText('');
                  const where =
                    result.batches.length === 0
                      ? ''
                      : ` into ${String(result.batches.length)} batch${result.batches.length === 1 ? '' : 'es'}`;
                  setMessage(
                    `Added ${String(result.added)} prospect${result.added === 1 ? '' : 's'}${where}. Skipped ${String(result.skipped)} already in the Hub.${result.failed === 0 ? '' : ` Failed ${String(result.failed)}: ${result.failures.join('; ')}`}`,
                  );
                },
                onError: (cause) =>
                  setError(
                    cause.message === 'Give the batch a name.' ||
                      cause.message === 'A batch with that name already exists.'
                      ? cause.message
                      : prospectErrorMessage(cause.message),
                  ),
              },
            )
          }
          disabled={add.isPending || rows.length === 0 || invalidBatch}
        >
          {add.isPending ? 'Adding…' : `Add ${String(rows.length)}`}
        </Button>
        {message !== '' && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            {message}
          </span>
        )}
      </div>
    </Drawer>
  );
}
